/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/Sections/AreasOfInterest/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../../../utils/logger.js";
import React, { useState, useEffect, useRef } from "react";
import styles from "./AreasOfInterest.module.css";
import { useTranslation } from "react-i18next";
import { getAreasOfInterestOptions } from "../../../data/selectOptions";
import { shouldHideFieldForProfile } from "../../../utils/common";

const AreasOfInterest = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const { t } = useTranslation();
  const onValidationChangeRef = useRef(onValidationChange);
  const [selectedAreas, setSelectedAreas] = useState([]);

  const isTablet = window.innerWidth <= 1023;
  const areasOfInterest = getAreasOfInterestOptions(t);

  // Update the ref when onValidationChange changes
  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  }, [onValidationChange]);

  useEffect(() => {
    if (onValidationChangeRef.current) {
      onValidationChangeRef.current(false);
    }
  }, []);

  useEffect(() => {
    if (company && company?.areasDeInteres) {
      setSelectedAreas(company?.areasDeInteres);
    }
  }, [company]);

  const handleCheckboxChange = (value) => {
    let newSelectedAreas;

    logger.info("selectedAreas", { data: selectedAreas });

    if (selectedAreas.includes(value)) {
      newSelectedAreas = selectedAreas.filter((area) => area !== value);
    } else {
      newSelectedAreas = [...selectedAreas, value];
    }

    setSelectedAreas(newSelectedAreas);

    if (onFieldChange) {
      onFieldChange("areasDeInteres", newSelectedAreas);
    }
  };

  const renderCheckboxRows = () => {
    const rows = [];
    const itemsPerRow = isTablet ? 4 : 5;
    for (let i = 0; i < areasOfInterest.length; i += itemsPerRow) {
      const rowItems = areasOfInterest.slice(i, i + itemsPerRow);
      rows.push(
        <div key={`row-${i}`} className={styles.checkboxRow}>
          {rowItems.map((area) => (
            <div key={area.value} className={styles.checkboxItem}>
              <input
                type="checkbox"
                id={area.value}
                className={styles.checkbox}
                checked={selectedAreas.includes(area.value)}
                onChange={() => handleCheckboxChange(area.value)}
              />
              <label htmlFor={area.value} className={styles.checkboxLabel}>
                {t(
                  `components.areasOfInterest.areaOfInterestOption${
                    area.value + 1
                  }`,
                )}
              </label>
            </div>
          ))}
        </div>,
      );
    }
    return rows;
  };

  // Hide entire section for Profile 2 and 3
  if (shouldHideFieldForProfile("areasDeInteres", selectedProfile)) {
    return null;
  }

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.areasOfInterest.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={styles.checkboxContainer}>{renderCheckboxRows()}</div>
    </div>
  );
};

export default AreasOfInterest;
